import { ChangeDetectionStrategy, Component, input } from '@angular/core';

/**
 * Data-gedreven reconstructie van de 66 Sides-tekening uit Ra's Rave
 * Cosmology (Bhan Tugh). Het hele figuur staat op één uniform grid: elke
 * vierkante cel is door beide diagonalen in vier even grote driehoeken
 * (N/E/S/W) gedeeld; de gekleurde kern gebruikt exact dezelfde cellen met
 * een kleur per driehoek. De kern-cijfers (grote 1/2/3/4 en de reeks 1-16)
 * zijn de Bhan Tugh-lagen en horen niet bij de side-nummers; alleen
 * side-vakken zijn highlightbaar.
 *
 * De tempelcel (3,3) is de uitzondering: een gouden blok bovenin, daarop een
 * celbrede driehoek (hoogte een halve cel) waarvan de basislijn nét onder
 * het celmidden ligt zodat er boven de punt open ruimte blijft, en een
 * bruine balk onder de basislijn.
 *
 * Coördinaten in celeenheden; de SVG schaalt mee met zijn container.
 */

interface DiagramShape {
  readonly d: string;
  readonly fill: string;
  readonly noStroke?: boolean;
  /** Side-nummer wanneer dit vlak een ster-side representeert. */
  readonly side?: number;
}

interface DiagramLabel {
  readonly text: string;
  readonly x: number;
  readonly y: number;
  readonly color: string;
  readonly size: number;
  readonly side?: number;
}

const LINE = '#7a7a7a';
const NUM = '#8a5a24';
const WHITE = '#ffffff';
const GREY_LIGHT = '#b7b6b4';
const GREY_MID = '#8a8a88';
const GREY_DARK = '#4a4a4a';
const GREY_DEEP = '#575755';
const BLACK = '#2a2a2a';
const BEIGE = '#e5decd';
const GOLD = '#ab9565';
const BROWN = '#615432';
const CORE_DARK = '#161616';
const CORE_LIGHT = '#ffffff';

/**
 * Drie cijfer-lagen: side-nummers (wit vak) het kleinst, kern-cijfers op de
 * grijze/zwarte driehoeken een tik groter, en de vier grote Bhan Tugh-lagen
 * (1/2/3/4) daar weer boven.
 */
const SIDE_SIZE = 0.15;
const CORE_SIZE = 0.178;
const CORE_SIZE_BIG = 0.225;

type Pt = readonly [number, number];

/** Vak-spec: `side` = wit(/beige) side-vak; `fill` = kernkleur; `core` = Bhan Tugh-cijfer. */
interface QuadSpec {
  readonly side?: number;
  readonly fill?: string;
  readonly core?: number;
  readonly coreColor?: string;
  readonly coreSize?: number;
}

type CellSpec = Partial<Record<'N' | 'E' | 'S' | 'W', QuadSpec>>;

function buildDiagram(): { shapes: DiagramShape[]; labels: DiagramLabel[] } {
  const shapes: DiagramShape[] = [];
  const labels: DiagramLabel[] = [];

  const path = (pts: readonly Pt[]): string => `M${pts.map(p => p.join(',')).join('L')}Z`;

  const tri = (pts: readonly Pt[], fill: string, extra?: { side?: number; noStroke?: boolean }): void => {
    shapes.push({ d: path(pts), fill, ...(extra ?? {}) });
  };

  const lab = (text: string, x: number, y: number, color = NUM, size = 0.155, side?: number): void => {
    labels.push(side != null ? { text, x, y, color, size, side } : { text, x, y, color, size });
  };

  /** X-cel: tekent enkel de vakken uit de spec (ontbrekend = niet getekend). */
  const cell = (c: number, r: number, spec: CellSpec): void => {
    const cx = c + 0.5;
    const cy = r + 0.5;
    const quads: Record<'N' | 'E' | 'S' | 'W', { pts: readonly Pt[]; lx: number; ly: number }> = {
      N: { pts: [[c, r], [c + 1, r], [cx, cy]], lx: cx, ly: r + 0.3 },
      E: { pts: [[c + 1, r], [c + 1, r + 1], [cx, cy]], lx: c + 0.8, ly: cy + 0.05 },
      S: { pts: [[c, r + 1], [c + 1, r + 1], [cx, cy]], lx: cx, ly: r + 0.87 },
      W: { pts: [[c, r], [c, r + 1], [cx, cy]], lx: c + 0.2, ly: cy + 0.05 },
    };
    for (const key of ['N', 'E', 'S', 'W'] as const) {
      const quadSpec = spec[key];
      if (!quadSpec) {
        continue;
      }
      const quad = quads[key];
      tri(quad.pts, quadSpec.fill ?? WHITE, quadSpec.side != null ? { side: quadSpec.side } : undefined);
      if (quadSpec.side != null) {
        lab(String(quadSpec.side), quad.lx, quad.ly, NUM, SIDE_SIZE, quadSpec.side);
      }
      if (quadSpec.core != null) {
        lab(String(quadSpec.core), quad.lx, quad.ly, quadSpec.coreColor ?? CORE_DARK, quadSpec.coreSize ?? CORE_SIZE);
      }
    }
  };

  const side = (s: number): QuadSpec => ({ side: s });
  const sideTri = (s: number, pts: readonly Pt[], lx: number, ly: number): void => {
    tri(pts, WHITE, { side: s });
    lab(String(s), lx, ly, NUM, SIDE_SIZE, s);
  };

  // ── Witte rand-cellen ───────────────────────────────────────────
  cell(2, 0, { N: side(65), E: side(61), S: side(52), W: side(62) });
  cell(3, 0, { E: side(60), S: side(51) });
  cell(4, 0, { W: side(59), E: side(58), S: side(50) });
  cell(2, 1, { N: side(35), E: side(3), S: side(4), W: side(36) });
  cell(3, 1, { N: side(34), E: side(33), S: side(1), W: side(2) });
  cell(4, 1, { N: side(49), E: side(48), S: side(31), W: side(32) });
  cell(5, 1, { E: side(57), S: side(56), W: {} });
  cell(6, 1, { N: side(66), W: side(64) });
  cell(6, 2, { W: side(55) });
  cell(1, 2, { N: side(38), E: side(7), S: side(8), W: side(39) });
  cell(1, 3, { N: side(9), E: side(10), S: side(12), W: side(11) });
  cell(1, 4, { N: side(13), E: side(14), S: side(41), W: side(40) });
  cell(5, 2, { N: side(47), E: side(46), S: side(27), W: side(28) });
  cell(5, 3, { N: side(26), E: side(45), S: side(24), W: side(25) });
  cell(2, 5, { N: side(17), E: side(18), S: side(43), W: side(42) });
  cell(3, 5, { N: side(20), E: side(21), S: side(44), W: side(19) });

  // Halve diamonds + de grote 63-driehoek (54 rendert eroverheen)
  sideTri(63, [[0, 5], [1, 4], [1, 5]], 0.55, 4.9);
  sideTri(37, [[2, 1], [1.5, 1.5], [2, 2]], 1.75, 1.55);
  sideTri(53, [[1, 3], [0.5, 3.5], [1, 4]], 0.75, 3.55);
  sideTri(54, [[1, 4], [0.5, 4.5], [1, 5]], 0.75, 4.55);

  // ── Gekleurde kern (zelfde grid, kleur per driehoek) ────────────
  cell(2, 2, {
    N: side(5), W: side(6),
    E: { fill: GREY_MID, core: 6 }, S: { fill: GREY_MID, core: 7 },
  });
  cell(3, 2, {
    N: { fill: BEIGE }, W: { fill: GREY_MID, core: 5 },
    E: { fill: BLACK, core: 4, coreColor: CORE_LIGHT },
    S: { fill: GREY_DARK, core: 1, coreColor: CORE_LIGHT, coreSize: CORE_SIZE_BIG },
  });
  cell(4, 2, {
    N: side(30), E: side(29),
    W: { fill: BLACK, core: 3, coreColor: CORE_LIGHT },
    S: { fill: BLACK, core: 2, coreColor: CORE_LIGHT },
  });
  cell(2, 3, {
    N: { fill: GREY_MID, core: 8 }, W: { fill: BEIGE },
    E: { fill: GREY_LIGHT, core: 2, coreSize: CORE_SIZE_BIG },
    S: { fill: GREY_LIGHT, core: 9 },
  });
  cell(4, 3, {
    N: { fill: BLACK, core: 1, coreColor: CORE_LIGHT },
    W: { fill: BLACK, core: 4, coreColor: CORE_LIGHT, coreSize: CORE_SIZE_BIG },
    E: { fill: BEIGE },
    S: { fill: GREY_DEEP, core: 16, coreColor: CORE_LIGHT },
  });
  cell(2, 4, {
    N: { fill: GREY_LIGHT, core: 10 }, W: side(15),
    E: { fill: GREY_LIGHT, core: 11 }, S: side(16),
  });
  cell(3, 4, {
    N: { fill: GREY_MID, core: 3, coreSize: CORE_SIZE_BIG },
    W: { fill: GREY_LIGHT, core: 12 },
    E: { fill: GREY_DEEP, core: 13, coreColor: CORE_LIGHT },
    S: { fill: BEIGE },
  });
  cell(4, 4, {
    N: { fill: GREY_DEEP, core: 15, coreColor: CORE_LIGHT },
    W: { fill: GREY_DEEP, core: 14, coreColor: CORE_LIGHT },
    E: side(23), S: side(22),
  });

  // ── Tempelcel (3,3): volledig gevuld, alle vormen celbreed ──────
  // Goud boven, de celbrede driehoek (hoogte een halve cel, basislijn nét
  // onder het celmidden zodat er goud boven de punt blijft) en bruin
  // eronder tot de celbodem — zonder witte randen of spleten.
  // Bruin start een fractie boven de goudgrens zodat anti-aliasing er geen
  // haarlijn tussen kan laten doorschemeren.
  tri([[3, 3], [4, 3], [4, 3.575], [3, 3.575]], GOLD, { noStroke: true });
  tri([[3, 3.565], [4, 3.565], [4, 4], [3, 4]], BROWN, { noStroke: true });
  tri([[3, 3.575], [3.5, 3.075], [4, 3.575]], BEIGE, { noStroke: true });

  return { shapes, labels };
}

const DIAGRAM = buildDiagram();

/**
 * De 66 Sides-tekening als schaalbare SVG. `highlightSide` markeert het vak
 * van die side (accentkleur + dikkere rand), bv. de fixed star van de chart.
 */
@Component({
  selector: 'hd-sixty-six-sides-diagram',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <svg viewBox="-0.15 -0.25 7.45 6.5" xmlns="http://www.w3.org/2000/svg" role="img" [attr.aria-label]="ariaLabel()">
      @for (shape of shapes; track $index) {
        <path
          [attr.d]="shape.d"
          [attr.fill]="shape.fill"
          [attr.stroke]="shape.noStroke ? 'none' : lineColor"
          [class.is-highlighted]="shape.side != null && shape.side === highlightSide()"
          stroke-width="0.013"
          stroke-linejoin="round"
        />
      }
      @for (l of labels; track $index) {
        <text
          [attr.x]="l.x"
          [attr.y]="l.y"
          [attr.fill]="l.side != null && l.side === highlightSide() ? highlightColor : l.color"
          [attr.font-size]="l.size"
          text-anchor="middle"
        >{{ l.text }}</text>
      }
    </svg>
  `,
  styles: `
    :host {
      display: block;
    }

    svg {
      display: block;
      width: 100%;
      height: auto;
    }

    text {
      font-family: Arial, sans-serif;
      font-weight: 700;
    }

    path.is-highlighted {
      fill: #f6d9d2;
      stroke: #c0392b;
      stroke-width: 0.035;
    }
  `,
})
export class HdSixtySixSidesDiagramComponent {
  /** Side-nummer (1..66) om te markeren, of `null` voor geen markering. */
  readonly highlightSide = input<number | null>(null);
  readonly ariaLabel = input<string>('66 Sides');

  protected readonly shapes = DIAGRAM.shapes;
  protected readonly labels = DIAGRAM.labels;
  protected readonly lineColor = LINE;
  protected readonly highlightColor = '#c0392b';
}
